import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Banknote,
  Calculator,
  CheckCircle2,
  Clock3,
  History,
  Landmark,
  Plus,
  ReceiptText,
  RefreshCw,
  Save,
  Settings,
  ShieldCheck,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Table from '../ui/Table';
import { supabase } from '../../lib/supabase';
import { currency } from '../../utils/formatters';

const tabs = [
  { id: 'executivo', label: 'Executivo', icon: Landmark },
  { id: 'equipe', label: 'Pagamentos da Equipe', icon: Users },
  { id: 'vendas', label: 'Vendas', icon: Banknote },
  { id: 'extrato', label: 'Extrato', icon: ReceiptText },
  { id: 'config', label: 'Configuracoes', icon: Settings },
  { id: 'simulador', label: 'Simulador', icon: Calculator },
];

const expenseCategories = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'infraestrutura', label: 'Infraestrutura' },
  { value: 'servidor', label: 'Servidor' },
  { value: 'apis', label: 'APIs' },
  { value: 'dominio', label: 'Dominio' },
  { value: 'operacional', label: 'Operacional' },
  { value: 'retirada_societaria', label: 'Retirada societaria' },
  { value: 'equipe', label: 'Equipe' },
  { value: 'reembolso', label: 'Reembolso' },
  { value: 'impostos', label: 'Impostos' },
  { value: 'outros', label: 'Outros' },
];

const paymentMethods = [
  { value: 'pix', label: 'Pix' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'outro', label: 'Outro' },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function currentMonthISO() {
  return new Date().toISOString().slice(0, 7);
}

function monthStart(month) {
  return `${month || currentMonthISO()}-01`;
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(`${String(value).split('T')[0]}T00:00:00`).toLocaleDateString('pt-BR');
}

function toNumber(value) {
  const normalized = String(value ?? '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function pct(value) {
  return `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

function StatusBadge({ status }) {
  const value = status || 'pendente';
  const tone = {
    pago: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    parcial: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    pendente: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
    cancelado: 'bg-red-500/15 text-red-300 border-red-500/30',
  }[value] || 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30';
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>{value}</span>;
}

function Kpi({ icon: Icon, label, value, helper, className = '' }) {
  return (
    <Card className={`min-h-[126px] ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
          <p className="mt-2 break-words font-display text-2xl leading-tight text-white sm:text-3xl">{value}</p>
          {helper && <p className="mt-2 text-sm text-zinc-400">{helper}</p>}
        </div>
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-surface2 text-orange-300">
          <Icon size={20} />
        </div>
      </div>
    </Card>
  );
}

export default function GDNFinancePanel({ plans = [] }) {
  const [activeTab, setActiveTab] = useState('executivo');
  const [month, setMonth] = useState(currentMonthISO());
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [balances, setBalances] = useState([]);
  const [salesBalances, setSalesBalances] = useState([]);
  const [salesExtract, setSalesExtract] = useState([]);
  const [cash, setCash] = useState({ entradas: 0, saidas: 0, saldo_operacional: 0 });
  const [monthly, setMonthly] = useState([]);
  const [extract, setExtract] = useState([]);
  const [audit, setAudit] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    reserve_percent: 10,
    marketing_percent: 10,
    company_percent: 40,
    team_percent: 60,
    estimated_monthly_costs: 0,
    payment_fee_percent: 5,
    tax_percent: 0,
    variable_cost_per_client: 0,
    minimum_profit_percent: 20,
    commission_safety_percent: 50,
  });
  const [participantForm, setParticipantForm] = useState({
    id: '',
    nome: '',
    percentual: '',
    funcao: '',
    observacao: '',
    prioridade_pagamento: 100,
    ativo: true,
  });
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    valor: '',
    data_pagamento: todayISO(),
    forma_pagamento: 'pix',
    observacao: '',
    quitar_saldo: false,
  });
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    valor: '',
    categoria: 'operacional',
    descricao: '',
    data: todayISO(),
    comprovante_url: '',
  });
  const [historyTarget, setHistoryTarget] = useState(null);
  const [period, setPeriod] = useState('mes');
  const [customStart, setCustomStart] = useState(todayISO());
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [simulator, setSimulator] = useState({
    starter: 10,
    pro: 20,
    premium: 5,
    commission_percent: 10,
    commission_months: 6,
  });

  const selectedCompetence = monthStart(month);

  async function loadFinance() {
    setLoading(true);
    try {
      const [
        settingsRes,
        participantsRes,
        balancesRes,
        salesBalancesRes,
        salesExtractRes,
        cashRes,
        monthlyRes,
        extractRes,
        auditRes,
      ] = await Promise.all([
        supabase.from('gdn_finance_settings').select('*').single(),
        supabase.from('gdn_finance_participants').select('*').is('deleted_at', null).order('prioridade_pagamento', { ascending: true }),
        supabase.from('gdn_finance_participant_balances').select('*'),
        supabase.from('gdn_finance_sales_balances').select('*'),
        supabase.from('gdn_finance_sales_extract').select('*').order('created_at', { ascending: false }).limit(250),
        supabase.from('gdn_finance_cash_summary').select('*').single(),
        supabase.from('gdn_finance_monthly_summary').select('*').order('competencia', { ascending: false }),
        supabase.from('gdn_finance_extract').select('*').order('data_movimento', { ascending: false }).order('created_at', { ascending: false }).limit(250),
        supabase.from('gdn_finance_audit_log').select('*').order('created_at', { ascending: false }).limit(40),
      ]);

      if (settingsRes.error) throw settingsRes.error;
      if (participantsRes.error) throw participantsRes.error;
      if (balancesRes.error) throw balancesRes.error;
      if (salesBalancesRes.error) throw salesBalancesRes.error;
      if (salesExtractRes.error) throw salesExtractRes.error;
      if (cashRes.error) throw cashRes.error;
      if (monthlyRes.error) throw monthlyRes.error;
      if (extractRes.error) throw extractRes.error;
      if (auditRes.error) throw auditRes.error;

      const nextSettings = settingsRes.data || {};
      setSettings(nextSettings);
      setSettingsForm({
        reserve_percent: Number(nextSettings.reserve_percent || 0),
        marketing_percent: Number(nextSettings.marketing_percent || 0),
        company_percent: Number(nextSettings.company_percent || 0),
        team_percent: Number(nextSettings.team_percent || 0),
        estimated_monthly_costs: Number(nextSettings.estimated_monthly_costs || 0),
        payment_fee_percent: Number(nextSettings.payment_fee_percent || 0),
        tax_percent: Number(nextSettings.tax_percent || 0),
        variable_cost_per_client: Number(nextSettings.variable_cost_per_client || 0),
        minimum_profit_percent: Number(nextSettings.minimum_profit_percent || 0),
        commission_safety_percent: Number(nextSettings.commission_safety_percent || 0),
      });
      setParticipants(participantsRes.data || []);
      setBalances(balancesRes.data || []);
      setSalesBalances(salesBalancesRes.data || []);
      setSalesExtract(salesExtractRes.data || []);
      setCash(cashRes.data || { entradas: 0, saidas: 0, saldo_operacional: 0 });
      setMonthly(monthlyRes.data || []);
      setExtract(extractRes.data || []);
      setAudit(auditRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar financeiro GDN:', error);
      toast.error(error.message || 'Não foi possível carregar o financeiro GDN');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadFinance();
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel('gdn-finance-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_finance_ledger' }, loadFinance)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_finance_participant_payments' }, loadFinance)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_finance_participant_allocations' }, loadFinance)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_finance_sales_allocations' }, loadFinance)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_sales_responsibilities' }, loadFinance)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_finance_settings' }, loadFinance)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gdn_finance_participants' }, loadFinance)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const monthlyCurrent = useMemo(() => (
    monthly.find((item) => item.competencia === selectedCompetence) || {
      receita_bruta: 0,
      reserva_gdn: 0,
      marketing: 0,
      parte_gdn: 0,
      parte_equipe: 0,
      saidas: 0,
      entradas_caixa: 0,
    }
  ), [monthly, selectedCompetence]);

  const participantRows = useMemo(() => (
    participants.map((participant) => {
      const balance = balances.find((item) => item.participant_id === participant.id && item.competencia === selectedCompetence);
      return {
        ...participant,
        valor_acumulado: Number(balance?.valor_acumulado || 0),
        valor_pago: Number(balance?.valor_pago || 0),
        valor_pendente: Number(balance?.valor_pendente || 0),
        valor_equipe: Number(balance?.valor_equipe || 0),
        valor_comissao_vendas: Number(balance?.valor_comissao_vendas || 0),
        status: balance?.status || 'pendente',
      };
    })
  ), [participants, balances, selectedCompetence]);

  const salesRows = useMemo(() => (
    participants.map((participant) => {
      const balance = salesBalances.find((item) => item.participant_id === participant.id && item.competencia === selectedCompetence);
      return {
        ...participant,
        clientes_venda: Number(balance?.clientes || 0),
        pagamentos_venda: Number(balance?.pagamentos || 0),
        valor_venda: Number(balance?.valor_acumulado || 0),
      };
    }).filter((participant) => participant.valor_venda > 0 || participant.clientes_venda > 0)
  ), [participants, salesBalances, selectedCompetence]);

  const totalPendingTeam = participantRows.reduce((total, item) => total + Number(item.valor_pendente || 0), 0);
  const totalSalesParticipation = salesRows.reduce((total, item) => total + Number(item.valor_venda || 0), 0);
  const selectedSalesExtract = salesExtract.filter((item) => item.competencia === selectedCompetence);
  const activePercentTotal = participants
    .filter((item) => item.ativo && !item.deleted_at)
    .reduce((total, item) => total + Number(item.percentual || 0), 0);
  const safeWithdrawal = Math.max(Number(cash.saldo_operacional || 0) - Number(settings?.estimated_monthly_costs || 0), 0);

  const filteredExtract = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    if (period === '7d') start.setDate(start.getDate() - 6);
    if (period === '30d') start.setDate(start.getDate() - 29);
    if (period === 'mes') {
      start.setDate(1);
    }
    if (period === 'custom') {
      start.setTime(new Date(`${customStart}T00:00:00`).getTime());
      end.setTime(new Date(`${customEnd}T23:59:59`).getTime());
    }

    return extract.filter((item) => {
      const date = new Date(`${item.data_movimento}T12:00:00`);
      return date >= start && date <= end;
    });
  }, [extract, period, customStart, customEnd]);

  const simulatorResult = useMemo(() => {
    const byCode = new Map(plans.map((plan) => [String(plan.codigo || plan.nome || '').toLowerCase(), Number(plan.valor_mensal || 0)]));
    const starterValue = byCode.get('starter') || 0;
    const proValue = byCode.get('pro') || 0;
    const premiumValue = byCode.get('premium') || 0;
    const clients = Number(simulator.starter || 0) + Number(simulator.pro || 0) + Number(simulator.premium || 0);
    const mrr = (Number(simulator.starter || 0) * starterValue)
      + (Number(simulator.pro || 0) * proValue)
      + (Number(simulator.premium || 0) * premiumValue);
    const reserve = mrr * Number(settingsForm.reserve_percent || 0) / 100;
    const marketing = mrr * Number(settingsForm.marketing_percent || 0) / 100;
    const paymentFees = mrr * Number(settingsForm.payment_fee_percent || 0) / 100;
    const taxes = mrr * Number(settingsForm.tax_percent || 0) / 100;
    const variableCosts = clients * Number(settingsForm.variable_cost_per_client || 0);
    const fixedCosts = Number(settingsForm.estimated_monthly_costs || 0);
    const minimumProfit = mrr * Number(settingsForm.minimum_profit_percent || 0) / 100;
    const distributable = Math.max(mrr - reserve - marketing, 0);
    const company = distributable * Number(settingsForm.company_percent || 0) / 100;
    const team = distributable * Number(settingsForm.team_percent || 0) / 100;
    const actualProfitBeforeCommission = mrr - reserve - marketing - team - paymentFees - taxes - variableCosts - fixedCosts;
    const commissionCapacity = Math.max(actualProfitBeforeCommission - minimumProfit, 0);
    const maximumCommissionPercent = mrr > 0 ? commissionCapacity / mrr * 100 : 0;
    const recommendedCommissionBudget = commissionCapacity * Number(settingsForm.commission_safety_percent || 0) / 100;
    const recommendedCommissionPercent = mrr > 0 ? recommendedCommissionBudget / mrr * 100 : 0;
    const proposedMonthlyCommission = mrr * Number(simulator.commission_percent || 0) / 100;
    const projectedCommissionPerSale = clients > 0
      ? (mrr / clients) * Number(simulator.commission_percent || 0) / 100 * Number(simulator.commission_months || 0)
      : 0;
    const commissionStatus = proposedMonthlyCommission <= recommendedCommissionBudget
      ? 'segura'
      : proposedMonthlyCommission <= commissionCapacity ? 'atencao' : 'perigosa';
    return {
      clients,
      mrr,
      arr: mrr * 12,
      reserve,
      marketing,
      paymentFees,
      taxes,
      variableCosts,
      fixedCosts,
      minimumProfit,
      company,
      team,
      actualProfitBeforeCommission,
      commissionCapacity,
      maximumCommissionPercent,
      recommendedCommissionBudget,
      recommendedCommissionPercent,
      proposedMonthlyCommission,
      projectedCommissionPerSale,
      commissionStatus,
    };
  }, [plans, simulator, settingsForm]);

  async function saveSettings() {
    const reserve = Number(settingsForm.reserve_percent || 0);
    const marketing = Number(settingsForm.marketing_percent || 0);
    const company = Number(settingsForm.company_percent || 0);
    const team = Number(settingsForm.team_percent || 0);

    if (reserve + marketing > 100) {
      toast.error('Reserva + marketing não podem passar de 100%.');
      return;
    }
    if (company + team !== 100) {
      toast.error('Divisao GDN + equipe precisa fechar em 100%.');
      return;
    }

    setSavingSettings(true);
    try {
      const { error } = await supabase
        .from('gdn_finance_settings')
        .upsert({
          id: true,
          reserve_percent: reserve,
          marketing_percent: marketing,
          company_percent: company,
          team_percent: team,
          estimated_monthly_costs: Number(settingsForm.estimated_monthly_costs || 0),
          payment_fee_percent: Number(settingsForm.payment_fee_percent || 0),
          tax_percent: Number(settingsForm.tax_percent || 0),
          variable_cost_per_client: Number(settingsForm.variable_cost_per_client || 0),
          minimum_profit_percent: Number(settingsForm.minimum_profit_percent || 0),
          commission_safety_percent: Number(settingsForm.commission_safety_percent || 0),
        });

      if (error) throw error;
      toast.success('Configuracao financeira salva.');
      loadFinance();
    } catch (error) {
      toast.error(error.message || 'Não foi possível salvar configuração.');
    } finally {
      setSavingSettings(false);
    }
  }

  function resetParticipantForm() {
    setParticipantForm({
      id: '',
      nome: '',
      percentual: '',
      funcao: '',
      observacao: '',
      prioridade_pagamento: 100,
      ativo: true,
    });
  }

  async function saveParticipant() {
    const nextParticipant = {
      ...participantForm,
      nome: participantForm.nome.trim(),
      percentual: Number(participantForm.percentual || 0),
      prioridade_pagamento: Number(participantForm.prioridade_pagamento || 100),
    };

    if (!nextParticipant.nome) {
      toast.error('Informe o nome do participante.');
      return;
    }

    const simulated = participantForm.id
      ? participants.map((item) => (item.id === participantForm.id ? { ...item, ...nextParticipant } : item))
      : [...participants, nextParticipant];
    const total = simulated.filter((item) => item.ativo && !item.deleted_at).reduce((sum, item) => sum + Number(item.percentual || 0), 0);

    if (Number(total.toFixed(2)) !== 100) {
      toast.error(`Participantes ativos precisam fechar em 100%. Total atual: ${pct(total)}.`);
      return;
    }

    try {
      const payload = {
        nome: nextParticipant.nome,
        percentual: nextParticipant.percentual,
        funcao: nextParticipant.funcao || null,
        observacao: nextParticipant.observacao || null,
        prioridade_pagamento: nextParticipant.prioridade_pagamento,
        ativo: Boolean(nextParticipant.ativo),
      };

      const { error } = participantForm.id
        ? await supabase.from('gdn_finance_participants').update(payload).eq('id', participantForm.id)
        : await supabase.from('gdn_finance_participants').insert(payload);

      if (error) throw error;
      toast.success('Participante salvo.');
      resetParticipantForm();
      loadFinance();
    } catch (error) {
      toast.error(error.message || 'Não foi possível salvar participante.');
    }
  }

  function openPaymentModal(participant) {
    setPaymentTarget(participant);
    setPaymentForm({
      valor: participant.valor_pendente ? String(participant.valor_pendente) : '',
      data_pagamento: todayISO(),
      forma_pagamento: 'pix',
      observacao: '',
      quitar_saldo: Boolean(participant.valor_pendente),
    });
  }

  async function registerParticipantPayment() {
    if (!paymentTarget) return;
    const amount = paymentForm.quitar_saldo ? paymentTarget.valor_pendente : toNumber(paymentForm.valor);

    if (amount <= 0) {
      toast.error('Informe um valor maior que zero.');
      return;
    }

    try {
      const { error } = await supabase.rpc('gdn_finance_register_participant_payment', {
        target_participant_id: paymentTarget.id,
        input_valor: amount,
        input_data_pagamento: paymentForm.data_pagamento,
        input_forma_pagamento: paymentForm.forma_pagamento,
        input_observacao: paymentForm.observacao || null,
        input_quitar_saldo: paymentForm.quitar_saldo,
      });

      if (error) throw error;
      toast.success('Pagamento registrado e ledger atualizado.');
      setPaymentTarget(null);
      loadFinance();
    } catch (error) {
      toast.error(error.message || 'Não foi possível registrar pagamento.');
    }
  }

  async function registerExpense() {
    const amount = toNumber(expenseForm.valor);
    if (amount <= 0) {
      toast.error('Informe o valor da saida.');
      return;
    }
    if (!expenseForm.descricao.trim()) {
      toast.error('Descreva a saida para manter o historico claro.');
      return;
    }

    try {
      const { error } = await supabase.rpc('gdn_finance_register_expense', {
        input_valor: amount,
        input_categoria: expenseForm.categoria,
        input_descricao: expenseForm.descricao,
        input_data: expenseForm.data,
        input_comprovante_url: expenseForm.comprovante_url || null,
        input_metadata: {},
      });

      if (error) throw error;
      toast.success('Saida registrada no ledger.');
      setExpenseOpen(false);
      setExpenseForm({ valor: '', categoria: 'operacional', descricao: '', data: todayISO(), comprovante_url: '' });
      loadFinance();
    } catch (error) {
      toast.error(error.message || 'Não foi possível registrar saída.');
    }
  }

  const withdrawalWarning = expenseForm.categoria === 'retirada_societaria' && toNumber(expenseForm.valor) > safeWithdrawal;
  const paymentAmount = paymentTarget ? (paymentForm.quitar_saldo ? paymentTarget.valor_pendente : toNumber(paymentForm.valor)) : 0;
  const remainingAfterPayment = paymentTarget ? Math.max(Number(paymentTarget.valor_pendente || 0) - paymentAmount, 0) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-white">Financeiro societario GDN</h3>
          <p className="text-sm text-zinc-400">Ledger interno, divisoes, reservas, pagamentos da equipe e caixa operacional.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input type="month" className="w-[160px]" value={month} onChange={(event) => setMonth(event.target.value)} />
          <Button variant="ghost" onClick={loadFinance}>
            <RefreshCw size={15} />
            Atualizar
          </Button>
          <Button onClick={() => setExpenseOpen(true)}>
            <Plus size={15} />
            Registrar Saida
          </Button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex min-h-10 flex-shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition ${activeTab === tab.id ? 'border-orange-500/50 bg-orange-500/15 text-orange-200' : 'border-border bg-surface text-zinc-300 hover:bg-surface2'}`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'executivo' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={Wallet} label="Saldo operacional" value={currency(cash.saldo_operacional || 0)} helper="Entradas menos saidas reais do ledger." />
            <Kpi icon={TrendingUp} label="Receita da competencia" value={currency(monthlyCurrent.receita_bruta || 0)} helper={`Competencia ${formatDate(selectedCompetence)}`} />
            <Kpi icon={ShieldCheck} label="Reserva + marketing" value={currency(Number(monthlyCurrent.reserva_gdn || 0) + Number(monthlyCurrent.marketing || 0))} helper={`${pct(settings?.reserve_percent)} reserva, ${pct(settings?.marketing_percent)} marketing`} />
            <Kpi icon={Clock3} label="Equipe pendente" value={currency(totalPendingTeam)} helper="Saldo a pagar aos participantes no mes." />
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <Card>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-white">Fechamento da competencia</h4>
                  <p className="text-sm text-zinc-400">Valores contabilizados automaticamente apos pagamento aprovado.</p>
                </div>
                <Badge>{month}</Badge>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-border bg-surface2 p-3">
                  <p className="text-xs text-zinc-500">Distribuivel</p>
                  <p className="mt-1 text-xl font-semibold">{currency(Number(monthlyCurrent.receita_bruta || 0) - Number(monthlyCurrent.reserva_gdn || 0) - Number(monthlyCurrent.marketing || 0))}</p>
                </div>
                <div className="rounded-lg border border-border bg-surface2 p-3">
                  <p className="text-xs text-zinc-500">Parte GDN</p>
                  <p className="mt-1 text-xl font-semibold">{currency(monthlyCurrent.parte_gdn || 0)}</p>
                </div>
                <div className="rounded-lg border border-border bg-surface2 p-3">
                  <p className="text-xs text-zinc-500">Parte equipe</p>
                  <p className="mt-1 text-xl font-semibold">{currency(monthlyCurrent.parte_equipe || 0)}</p>
                </div>
              </div>
            </Card>

            <Card className={safeWithdrawal <= 0 ? 'border-red-500/40' : ''}>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-surface2 text-orange-300">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Alerta de caixa</h4>
                  <p className="mt-1 text-sm text-zinc-400">Custos previstos: {currency(settings?.estimated_monthly_costs || 0)}</p>
                  <p className="mt-2 text-2xl font-semibold">{currency(safeWithdrawal)}</p>
                  <p className="text-sm text-zinc-400">Maximo seguro estimado para retirada agora.</p>
                </div>
              </div>
            </Card>
          </div>

          <Card>
            <h4 className="mb-3 font-semibold text-white">Ultimas movimentacoes</h4>
            <Table
              columns={['Data', 'Tipo', 'Categoria', 'Descricao', 'Valor']}
              rows={extract.slice(0, 8)}
              loading={loading}
              emptyMessage="Nenhuma movimentacao no ledger"
              renderRow={(item) => (
                <tr key={item.id} className="border-t border-border hover:bg-surface2">
                  <td className="px-3 py-2">{formatDate(item.data_movimento)}</td>
                  <td className="px-3 py-2"><Badge>{item.natureza}</Badge></td>
                  <td className="px-3 py-2">{item.categoria}</td>
                  <td className="px-3 py-2">{item.descricao}</td>
                  <td className={`px-3 py-2 font-semibold ${item.natureza === 'saida' ? 'text-red-300' : item.natureza === 'entrada' ? 'text-emerald-300' : 'text-zinc-300'}`}>
                    {item.natureza === 'saida' ? '-' : item.natureza === 'entrada' ? '+' : ''}{currency(item.valor)}
                  </td>
                </tr>
              )}
            />
          </Card>
        </div>
      )}

      {activeTab === 'equipe' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {participantRows.map((participant) => (
              <Card key={participant.id} className="min-h-[250px]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500">{participant.funcao || 'Participante'}</p>
                    <h4 className="mt-1 text-lg font-semibold text-white">{participant.nome}</h4>
                    <p className="text-sm text-zinc-400">{pct(participant.percentual)} da equipe</p>
                  </div>
                  <StatusBadge status={participant.status} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-surface2 p-3">
                    <p className="text-xs text-zinc-500">Disponivel</p>
                    <p className="font-semibold text-emerald-300">{currency(participant.valor_pendente)}</p>
                  </div>
                  <div className="rounded-lg bg-surface2 p-3">
                    <p className="text-xs text-zinc-500">Acumulado</p>
                    <p className="font-semibold">{currency(participant.valor_acumulado)}</p>
                  </div>
                  <div className="rounded-lg bg-surface2 p-3">
                    <p className="text-xs text-zinc-500">Pago</p>
                    <p className="font-semibold">{currency(participant.valor_pago)}</p>
                  </div>
                  <div className="rounded-lg bg-surface2 p-3">
                    <p className="text-xs text-zinc-500">Pendente</p>
                    <p className="font-semibold text-amber-300">{currency(participant.valor_pendente)}</p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-zinc-500">
                  Divisao da equipe: {currency(participant.valor_equipe)} · Comissao de vendas: {currency(participant.valor_comissao_vendas)}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="ghost" onClick={() => setHistoryTarget(participant)}>
                    <History size={15} />
                    Ver Historico
                  </Button>
                  <Button onClick={() => openPaymentModal(participant)} disabled={participant.valor_pendente <= 0}>
                    <CheckCircle2 size={15} />
                    Registrar Pagamento
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'vendas' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Kpi icon={Banknote} label="Participacao de vendas" value={currency(totalSalesParticipation)} helper="Total gerado por mensalidades aprovadas no mes." />
            <Kpi icon={Users} label="Responsaveis com venda" value={salesRows.length} helper="Pessoas com participacao recorrente no periodo." />
            <Kpi icon={ReceiptText} label="Pagamentos vinculados" value={selectedSalesExtract.length} helper="Mensalidades que geraram participacao por venda." />
          </div>

          <Card>
            <h4 className="mb-3 font-semibold text-white">Resumo por responsavel</h4>
            <Table
              columns={['Responsavel', 'Funcao', 'Clientes', 'Pagamentos', 'Valor gerado']}
              rows={salesRows}
              loading={loading}
              emptyMessage="Nenhuma participacao de venda gerada neste mes"
              renderRow={(item) => (
                <tr key={item.id} className="border-t border-border hover:bg-surface2">
                  <td className="px-3 py-2 font-medium text-white">{item.nome}</td>
                  <td className="px-3 py-2">{item.funcao || '-'}</td>
                  <td className="px-3 py-2">{item.clientes_venda}</td>
                  <td className="px-3 py-2">{item.pagamentos_venda}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-300">{currency(item.valor_venda)}</td>
                </tr>
              )}
            />
          </Card>

          <Card>
            <h4 className="mb-3 font-semibold text-white">Detalhe por cliente</h4>
            <Table
              columns={['Cliente', 'Responsavel', 'Recebido', 'Base', 'Regra', 'Parcela', 'Valor']}
              rows={selectedSalesExtract}
              loading={loading}
              emptyMessage="Nenhum detalhe de venda no periodo"
              renderRow={(item) => (
                <tr key={item.id} className="border-t border-border hover:bg-surface2">
                  <td className="px-3 py-2">{item.nome_oficina || '-'}</td>
                  <td className="px-3 py-2">{item.participant_nome || '-'}</td>
                  <td className="px-3 py-2">{currency(item.gross_amount)}</td>
                  <td className="px-3 py-2">
                    {currency(item.base_amount || item.gross_amount)}
                    <span className="block text-xs text-zinc-500">
                      {item.base_calculo === 'mensalidade_equivalente' ? 'mensalidade equivalente' : 'valor recebido'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {pct(item.percentual)}
                    {Number(item.valor_fixo || 0) > 0 ? ` + ${currency(item.valor_fixo)}` : ''}
                  </td>
                  <td className="px-3 py-2">{item.numero_pagamento || '-'}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-300">{currency(item.valor_acumulado)}</td>
                </tr>
              )}
            />
          </Card>
        </div>
      )}

      {activeTab === 'extrato' && (
        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="font-semibold text-white">Extrato financeiro</h4>
              <p className="text-sm text-zinc-400">Entrada, saida e lancamentos contabeis sem apagar historico.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={period} onChange={(event) => setPeriod(event.target.value)} options={[
                { value: 'hoje', label: 'Hoje' },
                { value: '7d', label: '7 dias' },
                { value: '30d', label: '30 dias' },
                { value: 'mes', label: 'Mes atual' },
                { value: 'custom', label: 'Personalizado' },
              ]} />
              {period === 'custom' && (
                <>
                  <Input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} />
                  <Input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} />
                </>
              )}
            </div>
          </div>
          <Table
            columns={['Data', 'Natureza', 'Tipo', 'Categoria', 'Participante', 'Descricao', 'Valor']}
            rows={filteredExtract}
            loading={loading}
            emptyMessage="Nenhuma movimentacao no periodo"
            renderRow={(item) => (
              <tr key={item.id} className="border-t border-border hover:bg-surface2">
                <td className="px-3 py-2">{formatDate(item.data_movimento)}</td>
                <td className="px-3 py-2"><Badge>{item.natureza}</Badge></td>
                <td className="px-3 py-2">{item.tipo}</td>
                <td className="px-3 py-2">{item.categoria}</td>
                <td className="px-3 py-2">{item.participante_nome || '-'}</td>
                <td className="px-3 py-2">{item.descricao}</td>
                <td className={`px-3 py-2 font-semibold ${item.natureza === 'saida' ? 'text-red-300' : item.natureza === 'entrada' ? 'text-emerald-300' : 'text-zinc-300'}`}>
                  {item.natureza === 'saida' ? '-' : item.natureza === 'entrada' ? '+' : ''}{currency(item.valor)}
                </td>
              </tr>
            )}
          />
        </Card>
      )}

      {activeTab === 'config' && (
        <div className="grid gap-4 xl:grid-cols-[1fr_1.1fr]">
          <Card>
            <h4 className="font-semibold text-white">Regras de divisao</h4>
            <p className="mb-4 text-sm text-zinc-400">Aplicadas no backend assim que um pagamento e aprovado.</p>
            <div className="grid gap-3 md:grid-cols-2">
              <Input type="number" placeholder="Reserva GDN (%)" value={settingsForm.reserve_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, reserve_percent: event.target.value }))} />
              <Input type="number" placeholder="Marketing (%)" value={settingsForm.marketing_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, marketing_percent: event.target.value }))} />
              <Input type="number" placeholder="Parte GDN (%)" value={settingsForm.company_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, company_percent: event.target.value }))} />
              <Input type="number" placeholder="Parte equipe (%)" value={settingsForm.team_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, team_percent: event.target.value }))} />
              <Input className="md:col-span-2" type="number" placeholder="Custos previstos mensais" value={settingsForm.estimated_monthly_costs} onChange={(event) => setSettingsForm((prev) => ({ ...prev, estimated_monthly_costs: event.target.value }))} />
              <Input type="number" min="0" max="100" placeholder="Taxas de pagamento (%)" value={settingsForm.payment_fee_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, payment_fee_percent: event.target.value }))} />
              <Input type="number" min="0" max="100" placeholder="Impostos sobre receita (%)" value={settingsForm.tax_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, tax_percent: event.target.value }))} />
              <Input type="number" min="0" placeholder="Custo variavel por cliente" value={settingsForm.variable_cost_per_client} onChange={(event) => setSettingsForm((prev) => ({ ...prev, variable_cost_per_client: event.target.value }))} />
              <Input type="number" min="0" max="100" placeholder="Lucro minimo desejado (%)" value={settingsForm.minimum_profit_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, minimum_profit_percent: event.target.value }))} />
              <Input className="md:col-span-2" type="number" min="0" max="100" placeholder="Margem de seguranca da comissao (%)" value={settingsForm.commission_safety_percent} onChange={(event) => setSettingsForm((prev) => ({ ...prev, commission_safety_percent: event.target.value }))} />
              <p className="md:col-span-2 text-xs text-zinc-500">
                A margem de seguranca define quanto da capacidade maxima pode ser sugerido como comissao. Ex.: 50% preserva metade da folga calculada.
              </p>
              <Button className="md:col-span-2" onClick={saveSettings} disabled={savingSettings}>
                <Save size={15} />
                {savingSettings ? 'Salvando...' : 'Salvar regras'}
              </Button>
            </div>
          </Card>

          <Card>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="font-semibold text-white">Participantes da equipe</h4>
                <p className="text-sm text-zinc-400">Ativos precisam fechar em 100%. Inativos não recebem novas divisões.</p>
              </div>
              <Badge>{pct(activePercentTotal)}</Badge>
            </div>
            <div className="mb-4 grid gap-2 md:grid-cols-6">
              <Input className="md:col-span-2" placeholder="Nome" value={participantForm.nome} onChange={(event) => setParticipantForm((prev) => ({ ...prev, nome: event.target.value }))} />
              <Input placeholder="%" type="number" value={participantForm.percentual} onChange={(event) => setParticipantForm((prev) => ({ ...prev, percentual: event.target.value }))} />
              <Input placeholder="Funcao" value={participantForm.funcao} onChange={(event) => setParticipantForm((prev) => ({ ...prev, funcao: event.target.value }))} />
              <Input placeholder="Prioridade" type="number" value={participantForm.prioridade_pagamento} onChange={(event) => setParticipantForm((prev) => ({ ...prev, prioridade_pagamento: event.target.value }))} />
              <label className="flex min-h-10 items-center gap-2 rounded-lg border border-border bg-surface2 px-3 text-sm text-zinc-300">
                <input type="checkbox" checked={participantForm.ativo} onChange={(event) => setParticipantForm((prev) => ({ ...prev, ativo: event.target.checked }))} />
                ativo
              </label>
              <Input className="md:col-span-5" placeholder="Observacao" value={participantForm.observacao} onChange={(event) => setParticipantForm((prev) => ({ ...prev, observacao: event.target.value }))} />
              <Button onClick={saveParticipant}>
                <Save size={15} />
                {participantForm.id ? 'Atualizar' : 'Adicionar'}
              </Button>
            </div>
            <Table
              columns={['Nome', 'Função', 'Percentual', 'Prioridade', 'Status', 'Ações']}
              rows={participants}
              loading={loading}
              emptyMessage="Nenhum participante cadastrado"
              renderRow={(participant) => (
                <tr key={participant.id} className="border-t border-border hover:bg-surface2">
                  <td className="px-3 py-2">{participant.nome}</td>
                  <td className="px-3 py-2">{participant.funcao || '-'}</td>
                  <td className="px-3 py-2">{pct(participant.percentual)}</td>
                  <td className="px-3 py-2">{participant.prioridade_pagamento}</td>
                  <td className="px-3 py-2"><Badge>{participant.ativo ? 'ativo' : 'inativo'}</Badge></td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setParticipantForm({
                        id: participant.id,
                        nome: participant.nome || '',
                        percentual: participant.percentual || '',
                        funcao: participant.funcao || '',
                        observacao: participant.observacao || '',
                        prioridade_pagamento: participant.prioridade_pagamento || 100,
                        ativo: participant.ativo,
                      })}
                    >
                      Editar
                    </Button>
                  </td>
                </tr>
              )}
            />
          </Card>

          <Card className="xl:col-span-2">
            <h4 className="mb-3 font-semibold text-white">Auditoria financeira recente</h4>
            <Table
              columns={['Data', 'Usuario', 'Acao', 'Tabela', 'Registro']}
              rows={audit}
              loading={loading}
              emptyMessage="Nenhum log de auditoria"
              renderRow={(item) => (
                <tr key={item.id} className="border-t border-border hover:bg-surface2">
                  <td className="px-3 py-2">{new Date(item.created_at).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2">{item.actor_email || '-'}</td>
                  <td className="px-3 py-2">{item.action}</td>
                  <td className="px-3 py-2">{item.table_name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">{item.record_id || '-'}</td>
                </tr>
              )}
            />
          </Card>
        </div>
      )}

      {activeTab === 'simulador' && (
        <div className="grid gap-4 xl:grid-cols-[420px_1fr]">
          <Card>
            <h4 className="font-semibold text-white">Projecao financeira</h4>
            <p className="mb-4 text-sm text-zinc-400">Use os valores reais dos planos e custos para descobrir uma faixa de comissao sustentavel.</p>
            <div className="space-y-3">
              <Input type="number" placeholder="Clientes Starter" value={simulator.starter} onChange={(event) => setSimulator((prev) => ({ ...prev, starter: event.target.value }))} />
              <Input type="number" placeholder="Clientes Pro" value={simulator.pro} onChange={(event) => setSimulator((prev) => ({ ...prev, pro: event.target.value }))} />
              <Input type="number" placeholder="Clientes Premium" value={simulator.premium} onChange={(event) => setSimulator((prev) => ({ ...prev, premium: event.target.value }))} />
              <Input type="number" min="0" max="100" placeholder="Comissao que deseja testar (%)" value={simulator.commission_percent} onChange={(event) => setSimulator((prev) => ({ ...prev, commission_percent: event.target.value }))} />
              <Input type="number" min="1" max="120" placeholder="Duracao da comissao (pagamentos)" value={simulator.commission_months} onChange={(event) => setSimulator((prev) => ({ ...prev, commission_months: event.target.value }))} />
            </div>
          </Card>
          <div className="grid gap-3 md:grid-cols-2">
            <Kpi icon={Banknote} label="MRR previsto" value={currency(simulatorResult.mrr)} />
            <Kpi icon={TrendingUp} label="ARR previsto" value={currency(simulatorResult.arr)} />
            <Kpi icon={ShieldCheck} label="Reserva" value={currency(simulatorResult.reserve)} />
            <Kpi icon={ReceiptText} label="Marketing" value={currency(simulatorResult.marketing)} />
            <Kpi icon={ReceiptText} label="Taxas + impostos" value={currency(simulatorResult.paymentFees + simulatorResult.taxes)} />
            <Kpi icon={Wallet} label="Custos fixos + variaveis" value={currency(simulatorResult.fixedCosts + simulatorResult.variableCosts)} />
            <Kpi icon={Landmark} label="Parte GDN" value={currency(simulatorResult.company)} />
            <Kpi icon={Users} label="Parte equipe" value={currency(simulatorResult.team)} />
            <Kpi icon={ShieldCheck} label="Lucro minimo protegido" value={currency(simulatorResult.minimumProfit)} />
            <Kpi
              icon={Calculator}
              label="Comissao recomendada"
              value={pct(simulatorResult.recommendedCommissionPercent)}
              helper={`${currency(simulatorResult.recommendedCommissionBudget)} por mes no cenario simulado.`}
            />
            <Kpi
              icon={AlertTriangle}
              label="Limite maximo calculado"
              value={pct(simulatorResult.maximumCommissionPercent)}
              helper="Acima deste percentual, a operacao invade o lucro minimo definido."
            />
            <Kpi
              icon={Banknote}
              label="Regra testada"
              value={currency(simulatorResult.proposedMonthlyCommission)}
              helper={`${pct(simulator.commission_percent)} por pagamento; custo medio estimado de ${currency(simulatorResult.projectedCommissionPerSale)} por venda ao longo de ${simulator.commission_months} pagamentos.`}
              className={simulatorResult.commissionStatus === 'perigosa' ? 'border-red-500/40' : simulatorResult.commissionStatus === 'atencao' ? 'border-amber-500/40' : 'border-emerald-500/40'}
            />
            <Card className={`md:col-span-2 ${simulatorResult.commissionStatus === 'perigosa' ? 'border-red-500/40' : simulatorResult.commissionStatus === 'atencao' ? 'border-amber-500/40' : 'border-emerald-500/40'}`}>
              <p className="text-xs uppercase tracking-wide text-zinc-500">Diagnostico da comissao testada</p>
              <p className="mt-2 text-xl font-semibold text-white">
                {simulatorResult.commissionStatus === 'segura' && 'Dentro da faixa recomendada'}
                {simulatorResult.commissionStatus === 'atencao' && 'Possivel, mas consome a margem de seguranca'}
                {simulatorResult.commissionStatus === 'perigosa' && 'Percentual acima da capacidade atual'}
              </p>
              <p className="mt-2 text-sm text-zinc-400">
                Resultado antes da comissao: {currency(simulatorResult.actualProfitBeforeCommission)}. Capacidade livre apos proteger o lucro minimo: {currency(simulatorResult.commissionCapacity)}.
              </p>
            </Card>
          </div>
        </div>
      )}

      <Modal open={Boolean(paymentTarget)} onClose={() => setPaymentTarget(null)} title="Registrar pagamento da equipe">
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-surface2 p-3">
            <p className="text-sm text-zinc-400">Voce esta pagando</p>
            <p className="mt-1 text-lg font-semibold text-white">{paymentTarget?.nome}</p>
            <p className="text-sm text-zinc-400">Saldo disponivel: {currency(paymentTarget?.valor_pendente || 0)}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Input type="number" placeholder="Valor pago" value={paymentForm.valor} disabled={paymentForm.quitar_saldo} onChange={(event) => setPaymentForm((prev) => ({ ...prev, valor: event.target.value }))} />
            <Input type="date" value={paymentForm.data_pagamento} onChange={(event) => setPaymentForm((prev) => ({ ...prev, data_pagamento: event.target.value }))} />
            <Select value={paymentForm.forma_pagamento} onChange={(event) => setPaymentForm((prev) => ({ ...prev, forma_pagamento: event.target.value }))} options={paymentMethods} />
            <label className="flex min-h-10 items-center gap-2 rounded-lg border border-border bg-surface2 px-3 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={paymentForm.quitar_saldo}
                onChange={(event) => setPaymentForm((prev) => ({
                  ...prev,
                  quitar_saldo: event.target.checked,
                  valor: event.target.checked ? String(paymentTarget?.valor_pendente || '') : prev.valor,
                }))}
              />
              quitar saldo total
            </label>
            <Input className="md:col-span-2" placeholder="Observacao" value={paymentForm.observacao} onChange={(event) => setPaymentForm((prev) => ({ ...prev, observacao: event.target.value }))} />
          </div>
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 text-sm">
            <p className="font-semibold text-orange-100">Resumo antes da confirmacao</p>
            <p className="mt-1 text-zinc-300">Valor: {currency(paymentAmount)}</p>
            <p className="text-zinc-300">Saldo restante: {currency(remainingAfterPayment)}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPaymentTarget(null)}>Cancelar</Button>
            <Button onClick={registerParticipantPayment}>Confirmar pagamento</Button>
          </div>
        </div>
      </Modal>

      <Modal open={expenseOpen} onClose={() => setExpenseOpen(false)} title="Registrar saida">
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Input type="number" placeholder="Valor" value={expenseForm.valor} onChange={(event) => setExpenseForm((prev) => ({ ...prev, valor: event.target.value }))} />
            <Input type="date" value={expenseForm.data} onChange={(event) => setExpenseForm((prev) => ({ ...prev, data: event.target.value }))} />
            <Select value={expenseForm.categoria} onChange={(event) => setExpenseForm((prev) => ({ ...prev, categoria: event.target.value }))} options={expenseCategories} />
            <Input placeholder="Comprovante opcional (URL)" value={expenseForm.comprovante_url} onChange={(event) => setExpenseForm((prev) => ({ ...prev, comprovante_url: event.target.value }))} />
            <Input className="md:col-span-2" placeholder="Descricao" value={expenseForm.descricao} onChange={(event) => setExpenseForm((prev) => ({ ...prev, descricao: event.target.value }))} />
          </div>
          {withdrawalWarning && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">
              Essa retirada pode comprometer o caixa operacional da GDN. Maximo seguro estimado: {currency(safeWithdrawal)}.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setExpenseOpen(false)}>Cancelar</Button>
            <Button onClick={registerExpense}>Registrar no ledger</Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(historyTarget)} onClose={() => setHistoryTarget(null)} title="Historico do participante">
        <Table
          columns={['Data', 'Categoria', 'Descricao', 'Valor']}
          rows={extract.filter((item) => item.participant_id === historyTarget?.id)}
          loading={loading}
          emptyMessage="Nenhum historico para este participante"
          renderRow={(item) => (
            <tr key={item.id} className="border-t border-border hover:bg-surface2">
              <td className="px-3 py-2">{formatDate(item.data_movimento)}</td>
              <td className="px-3 py-2">{item.categoria}</td>
              <td className="px-3 py-2">{item.descricao}</td>
              <td className="px-3 py-2">{currency(item.valor)}</td>
            </tr>
          )}
        />
      </Modal>
    </div>
  );
}
